import { useEffect, useRef, useState, useCallback } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { extractLucidId } from '../../utils/lucidgreen';

interface QRScannerProps {
  onLucidIdDecoded: (lucidId: string) => void;
  onInvalidQr: (payload: string) => void;
  onTimeout: () => void;
  onCancel: () => void;
}

const SCAN_TIMEOUT_MS = 15000;
const SCANNER_ELEMENT_ID = 'qr-scanner-region';

export function QRScanner({
  onLucidIdDecoded,
  onInvalidQr,
  onTimeout,
  onCancel,
}: QRScannerProps) {
  const [isStarting, setIsStarting] = useState(true);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasDecodedRef = useRef(false);
  const isMountedRef = useRef(true);

  const stopScanner = useCallback(async () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (scannerRef.current) {
      try {
        const state = scannerRef.current.getState();
        // State 2 = SCANNING, State 3 = PAUSED
        if (state === 2 || state === 3) {
          await scannerRef.current.stop();
        }
      } catch {
        // Scanner may already be stopped
      }
      scannerRef.current = null;
    }
  }, []);

  const handleCancel = useCallback(async () => {
    await stopScanner();
    onCancel();
  }, [stopScanner, onCancel]);

  useEffect(() => {
    isMountedRef.current = true;
    hasDecodedRef.current = false;

    const startScanner = async () => {
      const html5QrCode = new Html5Qrcode(SCANNER_ELEMENT_ID);
      scannerRef.current = html5QrCode;

      const onScanSuccess = async (decodedText: string) => {
        // Only process the first detected QR code
        if (hasDecodedRef.current) return;
        hasDecodedRef.current = true;

        // Stop the scanner immediately
        await stopScanner();

        if (!isMountedRef.current) return;

        const lucidId = extractLucidId(decodedText);
        if (lucidId) {
          onLucidIdDecoded(lucidId);
        } else {
          onInvalidQr(decodedText);
        }
      };

      const cameraConfig = { facingMode: 'environment' };
      const scanConfig = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
      };

      try {
        await html5QrCode.start(
          cameraConfig,
          scanConfig,
          onScanSuccess,
          // Silence scan errors (fires on every non-QR frame)
          () => {},
        );

        if (!isMountedRef.current) {
          await stopScanner();
          return;
        }

        setIsStarting(false);

        // Start the 15-second timeout
        timeoutRef.current = setTimeout(async () => {
          if (!hasDecodedRef.current && isMountedRef.current) {
            await stopScanner();
            onTimeout();
          }
        }, SCAN_TIMEOUT_MS);
      } catch {
        // Rear camera failed, try front-facing camera
        try {
          await html5QrCode.start(
            { facingMode: 'user' },
            scanConfig,
            onScanSuccess,
            () => {},
          );

          if (!isMountedRef.current) {
            await stopScanner();
            return;
          }

          setIsStarting(false);

          timeoutRef.current = setTimeout(async () => {
            if (!hasDecodedRef.current && isMountedRef.current) {
              await stopScanner();
              onTimeout();
            }
          }, SCAN_TIMEOUT_MS);
        } catch {
          // Both cameras failed — this shouldn't happen if permission was granted,
          // but handle gracefully by cancelling
          if (isMountedRef.current) {
            setIsStarting(false);
            onCancel();
          }
        }
      }
    };

    startScanner();

    return () => {
      isMountedRef.current = false;
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="qr-scanner">
      <div className="qr-scanner-viewfinder">
        <div id={SCANNER_ELEMENT_ID} className="qr-scanner-region" />
        {!isStarting && (
          <div className="qr-scanner-overlay" aria-hidden="true">
            <div className="qr-scanner-frame" />
          </div>
        )}
      </div>

      {isStarting && (
        <div className="qr-scanner-loading">
          <div className="loading-spinner" />
          <p>Starting camera…</p>
        </div>
      )}

      <div className="qr-scanner-actions">
        <button
          className="cta-button-secondary"
          onClick={handleCancel}
          type="button"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
